import { assetRequiresSynchronization, type NoteAsset } from '../assets/assetTypes'
import type { ActiveDraft, Note, NoteRevision, SyncOperation } from '../domain/note'
import type { SettingsRecord } from '../domain/settings'
import type { AppEnvironment } from '../supabase/config'
import type { LocalDataContinuitySnapshot } from './continuity'

export const LOCAL_RECOVERY_EXPORT_SCHEMA_VERSION = 2 as const

export interface LocalRecoveryExportSource {
  notes: Note[]
  activeDraft?: ActiveDraft
  revisions: NoteRevision[]
  outbox: SyncOperation[]
  settings: SettingsRecord[]
  assets: NoteAsset[]
}

export interface EncodedBlob {
  mimeType: string
  size: number
  base64: string
}

export interface ExportedAsset extends Omit<
  NoteAsset,
  'sourceBlob' | 'processedMasterBlob' | 'thumbnailBlob' | 'lastError'
> {
  sourceBlob: EncodedBlob | null
  processedMasterBlob: EncodedBlob | null
  thumbnailBlob: EncodedBlob | null
  lastError: null
}

export interface LocalRecoveryExport {
  format: 'minimany-local-recovery'
  schemaVersion: 1 | typeof LOCAL_RECOVERY_EXPORT_SCHEMA_VERSION
  exportedAt: string
  binding: {
    environment: AppEnvironment | null
    accountFingerprint: string | null
  }
  continuity: LocalDataContinuitySnapshot
  notes: Note[]
  activeDraft: ActiveDraft | null
  pendingOperations: SyncOperation[]
  pendingNoteRevisions: NoteRevision[]
  settings: SettingsRecord[]
  assets: ExportedAsset[]
  excluded: [
    'authentication sessions and tokens',
    'raw account identifiers, local bindings, and sync cursors',
    'derived caches and diagnostics',
  ]
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return btoa(binary)
}

async function encodeBlob(blob: Blob | null): Promise<EncodedBlob | null> {
  if (!blob) return null
  return {
    mimeType: blob.type || 'application/octet-stream',
    size: blob.size,
    base64: bytesToBase64(new Uint8Array(await blob.arrayBuffer())),
  }
}

async function exportAsset(asset: NoteAsset): Promise<ExportedAsset> {
  const [sourceBlob, processedMasterBlob, thumbnailBlob] = await Promise.all([
    encodeBlob(asset.sourceBlob),
    encodeBlob(asset.processedMasterBlob),
    encodeBlob(asset.thumbnailBlob),
  ])
  return {
    ...asset,
    sourceBlob,
    processedMasterBlob,
    thumbnailBlob,
    // Retry diagnostics are not required to recover content and may contain backend details.
    lastError: null,
  }
}

export async function buildLocalRecoveryExport(
  source: LocalRecoveryExportSource,
  continuity: LocalDataContinuitySnapshot,
  exportedAt = new Date(),
  binding: LocalRecoveryExport['binding'] = {
    environment: null,
    accountFingerprint: null,
  },
): Promise<LocalRecoveryExport> {
  const pendingRevisionIds = new Set(
    source.outbox
      .filter((operation) => operation.entityType === 'note')
      .map((operation) => operation.revisionId),
  )
  const localNoteIds = new Set([
    ...source.notes
      .filter((note) => note.localSyncState !== 'synced')
      .map((note) => note.id),
    ...source.outbox
      .filter((operation) => operation.entityType === 'note')
      .map((operation) => operation.entityId),
    ...source.assets
      .filter(assetRequiresSynchronization)
      .map((asset) => asset.noteId),
    ...(continuity.activeDraft.containsLocalChanges && source.activeDraft
      ? [source.activeDraft.noteId]
      : []),
  ])

  return {
    format: 'minimany-local-recovery',
    schemaVersion: LOCAL_RECOVERY_EXPORT_SCHEMA_VERSION,
    exportedAt: exportedAt.toISOString(),
    binding,
    continuity,
    notes: source.notes.filter((note) => localNoteIds.has(note.id)),
    activeDraft: continuity.activeDraft.containsLocalChanges
      ? source.activeDraft ?? null
      : null,
    pendingOperations: source.outbox,
    pendingNoteRevisions: source.revisions.filter((revision) =>
      pendingRevisionIds.has(revision.id),
    ),
    settings: source.settings.filter((settings) =>
      settings.localSyncState !== 'synced'
      && !(settings.recordType === 'global' && settings.modifiedAt === 0),
    ),
    assets: await Promise.all(
      source.assets.filter(assetRequiresSynchronization).map(exportAsset),
    ),
    excluded: [
      'authentication sessions and tokens',
      'raw account identifiers, local bindings, and sync cursors',
      'derived caches and diagnostics',
    ],
  }
}

export async function fingerprintRecoveryAccount(userId: string): Promise<string> {
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(userId)),
  )
  return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
